// AUTO-ESTRATTO da TeamDetail.jsx (refactor strutturale Fase 10.01) — nessun cambio di logica/UI/testid.
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";
import { formatMln, buybackReason } from "./helpers";
import { sanitizeDecimalInput } from "@/lib/amountInput";

export function RenewModal({ player, teamId, onClose, onDone }) {
    const newWage = player.purchase_wage ?? player.new_rule_wage ?? player.computed_wage ?? 0;
    const [years, setYears] = useState(player.contract_years || 3);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/teams/${teamId}/roster/${player.entry_id}/renew`, {
                contract_years: parseInt(years, 10),
            });
            onDone();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante il rinnovo");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur p-4"
            data-testid="renew-modal"
        >
            <div className="op-card max-w-sm w-full p-5">
                <div className="op-label mb-2" style={{ color: "rgb(var(--tc-7ce38b))" }}>// RINNOVO CONTRATTO</div>
                <div className="op-heading text-xl mb-1">{player.name}</div>
                <p className="text-sm text-t8f95a3 mb-3">
                    Stipendio al rinnovo (valore attuale su VG / ruolo / TM):
                </p>
                <div className="op-mono text-3xl font-bold text-t7ce38b mb-2" data-testid="renew-wage">
                    {formatMln(newWage)}
                </div>
                <p className="op-mono text-[10px] text-t5c6370 mb-4 leading-snug">
                    Regola: con ≥2 anni di contratto il rinnovo è ammesso solo se questo
                    stipendio è superiore a quello attuale. Se il contratto è in scadenza
                    (0-1 anno) il rinnovo è sempre permesso.
                </p>
                <label className="op-label text-[10px] text-t8f95a3">DURATA CONTRATTO</label>
                <select
                    className="op-input w-full mt-1 mb-4"
                    value={years}
                    onChange={(e) => setYears(parseInt(e.target.value, 10))}
                    data-testid="renew-years"
                >
                    {[1, 2, 3, 4, 5].map((y) => (
                        <option key={y} value={y}>{y} {y === 1 ? "anno" : "anni"}</option>
                    ))}
                </select>
                {err && (
                    <div className="op-card p-3 mb-3 text-sm text-tff4d4d border-tff4d4d/40" data-testid="renew-error">
                        {err}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button className="op-btn" onClick={onClose} disabled={busy} data-testid="renew-cancel">
                        Annulla
                    </button>
                    <button
                        className="op-btn op-btn-primary"
                        onClick={submit}
                        disabled={busy}
                        data-testid="renew-confirm"
                    >
                        {busy ? "Rinnovo…" : "Conferma rinnovo"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function MakeOfferModal({ target, sellerTeam, sellerTeamId, buyerTeamId, onClose, onDone }) {
    const [kind, setKind] = useState("transfer");
    // Pre-compila col prezzo richiesto in vetrina (se il giocatore è in vetrina), così
    // il compratore deve solo eventualmente modificarlo invece di ricordarselo.
    const [cashMln, setCashMln] = useState(
        target?.listing_price ? String(Math.round((target.listing_price / 1_000_000) * 100) / 100) : "",
    );
    const [buybackMln, setBuybackMln] = useState("");
    const [buyerPct, setBuyerPct] = useState(100);
    const [years, setYears] = useState(3);
    const [notes, setNotes] = useState("");
    // counterparties: list of {entry_id, name, vg}
    const [counterparties, setCounterparties] = useState([]);
    const [myRoster, setMyRoster] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [activeWindow, setActiveWindow] = useState(null);
    const [currentSeason, setCurrentSeason] = useState(null);

    // Anti-buyback: se il compratore è la squadra d'origine e siamo nella finestra di
    // divieto, è bloccato SOLO il trasferimento definitivo di ritorno; i prestiti (secco,
    // con diritto, con obbligo) restano ammessi.
    const buybackBlocked = !!buybackReason(target, buyerTeamId, currentSeason);

    useEffect(() => {
        // Se il definitivo è bloccato e per errore è selezionato, passa al prestito secco.
        if (buybackBlocked && kind === "transfer") setKind("loan");
    }, [buybackBlocked]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Fetch my roster (buyer team) for counterparties
        (async () => {
            try {
                const [r, w, s] = await Promise.all([
                    api.get(`/teams/${buyerTeamId}/roster`),
                    api.get("/market/windows/active"),
                    api.get("/admin/settings/season").catch(() => ({ data: {} })),
                ]);
                setMyRoster((r.data?.roster || []).filter((p) => !p.is_loaned_out));
                setActiveWindow(w.data);
                setCurrentSeason(s.data?.current_season ?? null);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [buyerTeamId]);

    const submit = async () => {
        setErr(null);
        if (kind === "transfer" && buybackBlocked) {
            setErr("Trasferimento definitivo bloccato dall'anti-buyback. Puoi riprenderlo solo in prestito.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                window_id: activeWindow?.id || null,
                kind,
                seller_team_id: sellerTeamId,
                buyer_team_id: buyerTeamId,
                player_id: target.entry_id,
                cash_amount: Math.round((parseFloat(cashMln) || 0) * 1_000_000),
                counterparty_players: (kind === "transfer" || kind === "loan_obligation")
                    ? counterparties.map((c) => ({ player_id: c.entry_id, name: c.name }))
                    : [],
                buyback_amount: kind === "transfer" || kind === "loan" ? 0 : Math.round((parseFloat(buybackMln) || 0) * 1_000_000),
                wage_split: kind === "transfer"
                    ? { buyer_pct: 100, seller_pct: 0 }
                    : { buyer_pct: buyerPct, seller_pct: 100 - buyerPct },
                contract_years: years,
                notes: notes || null,
            };
            await api.post("/market/deals", payload);
            onDone?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore creazione offerta");
        } finally {
            setBusy(false);
        }
    };

    const removeCp = (id) => setCounterparties((cs) => cs.filter((c) => c.entry_id !== id));
    const addCp = (p) => {
        setCounterparties((cs) => (cs.find((c) => c.entry_id === p.entry_id) ? cs : [...cs, p]));
        setShowPicker(false);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur p-4 overflow-y-auto"
            data-testid="make-offer-modal"
        >
            <div className="op-card max-w-lg w-full p-5 my-8 max-h-[90vh] overflow-y-auto">
                <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// NUOVA OFFERTA</div>
                <div className="op-heading text-2xl mt-2 mb-1">{target.name}</div>
                <div className="op-mono text-xs text-t8f95a3 mb-4">
                    Da <span className="text-tffffff">{sellerTeam?.name}</span> · VG {target.vg ?? "—"} · {trPositionShort(target.position) || "—"}
                </div>

                {activeWindow ? (
                    <div className="op-mono text-[10px] text-t2bb6ff mb-3">
                        Finestra: {activeWindow.name}
                    </div>
                ) : (
                    <div className="op-mono text-[10px] text-t8f95a3 mb-3">
                        Nessuna finestra attiva (offerta libera)
                    </div>
                )}

                {/* Stipendio dopo acquisto — formula nuova */}
                <div className="op-card bg-t0a0b0e border border-tffc93c/30 p-3 mb-3" data-testid="purchase-wage-preview">
                    <div className="op-label text-[10px]" style={{ color: "rgb(var(--tc-ffc93c))" }}>
                        // STIPENDIO DOPO L&apos;ACQUISTO
                    </div>
                    <div className="flex items-baseline justify-between mt-1.5">
                        <div className="op-mono text-2xl font-bold text-tffffff">
                            {formatMln(target.purchase_wage ?? target.new_rule_wage ?? target.computed_wage)}
                        </div>
                        <div className="op-mono text-[10px] text-t8f95a3 text-right leading-tight">
                            calcolato su:<br/>
                            VG {target.vg ?? "—"} · {trPositionShort(target.position) || "—"} · età {target.age ?? "—"}
                            {target.market_value ? ` · TM €${formatMln(target.market_value)}` : ""}
                        </div>
                    </div>
                    {target.current_wage > 0 && target.current_wage !== (target.purchase_wage ?? target.new_rule_wage) && (
                        <div className="op-mono text-[10px] text-t5c6370 mt-1">
                            Legacy attuale del venditore: €{formatMln(target.current_wage)} (perso dopo trasferimento)
                        </div>
                    )}
                </div>

                {/* Tipo operazione */}
                <label className="op-label block mb-1">Tipo operazione</label>
                <select
                    className="op-input mb-1"
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                    data-testid="deal-kind"
                >
                    <option value="transfer" disabled={buybackBlocked}>
                        Trasferimento definitivo{buybackBlocked ? " — bloccato (anti-buyback)" : ""}
                    </option>
                    <option value="loan">Prestito secco</option>
                    <option value="loan_obligation">Prestito + Obbligo di riscatto</option>
                    <option value="loan_option">Prestito + Diritto di riscatto</option>
                </select>
                {buybackBlocked && (
                    <div className="op-mono text-[10px] text-tff6b00 mb-3 leading-tight" data-testid="buyback-transfer-blocked">
                        {buybackReason(target, buyerTeamId, currentSeason)} Puoi però riprenderlo in <span className="text-tffffff">prestito</span> (secco, con diritto o con obbligo).
                    </div>
                )}
                {!buybackBlocked && <div className="mb-3" />}

                {/* Cash */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="op-label block mb-1">
                            {kind === "transfer" ? "Prezzo (mln)" : "Costo prestito (mln)"}
                        </label>
                        <input
                            type="text"
                            inputMode="decimal"
                            className="op-input"
                            value={cashMln}
                            onChange={(e) => setCashMln(sanitizeDecimalInput(e.target.value))}
                            data-testid="cash-amount"
                        />
                        {target?.listing_price != null && (
                            <div className="op-mono text-[10px] text-t7ce38b mt-1" data-testid="asking-price-hint">
                                Prezzo richiesto in vetrina: €{formatMln(target.listing_price)} · modificabile
                            </div>
                        )}
                    </div>
                    {(kind === "loan_obligation" || kind === "loan_option") && (
                        <div>
                            <label className="op-label block mb-1">Riscatto (mln)</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="op-input"
                                value={buybackMln}
                                onChange={(e) => setBuybackMln(sanitizeDecimalInput(e.target.value))}
                                data-testid="buyback-amount"
                            />
                        </div>
                    )}
                </div>

                {/* Loan-only options */}
                {kind !== "transfer" && (
                    <>
                        <label className="op-label block mb-1">Stipendio: chi lo paga</label>
                        <div className="flex items-center gap-3 mb-3">
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="5"
                                value={buyerPct}
                                onChange={(e) => setBuyerPct(parseInt(e.target.value, 10))}
                                className="flex-1"
                                data-testid="buyer-pct-slider"
                            />
                            <span className="op-mono text-[11px] text-right text-tffffff whitespace-nowrap" data-testid="wage-split-label">
                                Compratore {buyerPct}% / Venditore {100 - buyerPct}%
                            </span>
                        </div>
                    </>
                )}

                {/* Durata contratto giocatore — NON per il prestito secco (il giocatore torna) */}
                {kind !== "loan" && (
                    <>
                        <label className="op-label block mb-1">Durata contratto per {target.name} (anni)</label>
                        <select
                            className="op-input mb-3"
                            value={years}
                            onChange={(e) => setYears(parseInt(e.target.value, 10))}
                            data-testid="contract-years"
                        >
                            {[1, 2, 3, 4, 5].map((y) => (
                                <option key={y} value={y}>{y} {y === 1 ? "anno" : "anni"}</option>
                            ))}
                        </select>
                    </>
                )}

                {/* Contropartite */}
                {(kind === "transfer" || kind === "loan_obligation") && (
                    <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                            <label className="op-label">Contropartite (giocatori)</label>
                            <button
                                className="op-mono text-[10px] text-t2bb6ff hover:underline"
                                onClick={() => setShowPicker(true)}
                                data-testid="add-counterparty-btn"
                            >
                                + aggiungi
                            </button>
                        </div>
                        {kind === "loan_obligation" && (
                            <div className="op-mono text-[10px] text-t8f95a3 mb-1">
                                Le contropartite passano in modo <span className="text-tffffff">definitivo</span> alla squadra venditrice.
                            </div>
                        )}
                        {counterparties.length === 0 ? (
                            <div className="op-mono text-xs text-t5c6370">Nessuna contropartita</div>
                        ) : (
                            <div className="space-y-1">
                                {counterparties.map((c) => (
                                    <div key={c.entry_id} className="flex items-center justify-between p-2 border border-t22252a">
                                        <div className="text-sm">
                                            <span className="text-tffffff">{c.name}</span>
                                            <span className="op-mono text-xs text-t8f95a3 ml-2">VG {c.vg} · {trPositionShort(c.position)}</span>
                                        </div>
                                        <button
                                            className="op-mono text-[10px] text-tff4d4d hover:underline"
                                            onClick={() => removeCp(c.entry_id)}
                                        >
                                            rimuovi
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Note */}
                <label className="op-label block mb-1">Note (opzionale)</label>
                <textarea
                    className="op-input mb-3"
                    rows="2"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="notes"
                />

                {err && (
                    <div className="op-card p-3 mb-3 text-sm text-tff4d4d border-tff4d4d/40">{err}</div>
                )}

                <div className="flex justify-end gap-2 mt-3">
                    <button className="op-btn" onClick={onClose} disabled={busy} data-testid="modal-cancel-offer">
                        Annulla
                    </button>
                    <button
                        className="op-btn op-btn-primary"
                        onClick={submit}
                        disabled={busy}
                        data-testid="modal-send-offer"
                    >
                        {busy ? "Invio…" : "Invia offerta"}
                    </button>
                </div>
            </div>

            {showPicker && (
                <CounterpartyPicker
                    roster={myRoster}
                    excludeIds={counterparties.map((c) => c.entry_id)}
                    onPick={addCp}
                    onClose={() => setShowPicker(false)}
                />
            )}
        </div>
    );
}

export function CounterpartyPicker({ roster, excludeIds, onPick, onClose }) {
    const [q, setQ] = useState("");
    const filtered = roster
        .filter((p) => !excludeIds.includes(p.entry_id))
        .filter((p) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase()));
    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
            onClick={onClose}
            data-testid="counterparty-picker"
        >
            <div
                className="op-card max-w-md w-full p-4 max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="op-label mb-2">// SELEZIONA GIOCATORE</div>
                <input
                    className="op-input mb-3"
                    placeholder="Cerca nella tua rosa…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    autoFocus
                />
                <div className="overflow-y-auto space-y-1">
                    {filtered.map((p) => (
                        <button
                            key={p.entry_id}
                            onClick={() => onPick(p)}
                            className="w-full text-left p-2 border border-t22252a hover:border-t2bb6ff hover:bg-t2bb6ff/5 transition-colors"
                            data-testid={`pick-cp-${p.entry_id}`}
                        >
                            <div className="text-tffffff text-sm font-semibold flex items-center gap-2">
                                {p.name}
                                {p.is_ugly && (
                                    <span className="op-label text-[8px] px-1 py-0.5 border border-tff7a1a text-tff7a1a" data-testid={`cp-ugly-badge-${p.entry_id}`}>UGLY</span>
                                )}
                            </div>
                            <div className="op-mono text-xs text-t8f95a3">VG {p.vg} · {trPositionShort(p.position)}</div>
                        </button>
                    ))}
                    {filtered.length === 0 && (
                        <div className="op-mono text-xs text-t5c6370 text-center py-4">Nessun risultato</div>
                    )}
                </div>
            </div>
        </div>
    );
}


// -------------- Resolve Loan Modal (admin) --------------
export function ResolveLoanModal({ target, ownerTeamId, ownerTeamName, onClose, onDone }) {
    // target = roster entry on owner team with is_loaned_out=true
    // Includes: entry_id, name, playing_team_name (current host), pes_id
    const [allTeams, setAllTeams] = useState([]);
    const [action, setAction] = useState("sell"); // 'sell' | 'return'
    const [destTeamId, setDestTeamId] = useState("");
    const [feeMln, setFeeMln] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get("/teams").then((r) => {
            const teams = (r.data || []).filter((t) => t.id !== ownerTeamId);
            setAllTeams(teams);
            // Default destination = current host team (by playing_team_name match)
            if (target?.playing_team_name) {
                const match = teams.find((t) => t.name === target.playing_team_name);
                if (match) setDestTeamId(match.id);
            }
        }).catch(() => {});
    }, [ownerTeamId, target?.playing_team_name]);

    const submit = async () => {
        setBusy(true);
        setErr(null);
        try {
            const body = { owner_entry_id: target.entry_id, action };
            if (action === "sell") {
                if (!destTeamId) {
                    setErr("Seleziona la squadra di destinazione");
                    setBusy(false);
                    return;
                }
                body.destination_team_id = destTeamId;
                body.fee = Math.max(0, Math.round(Number(feeMln) * 1_000_000));
            }
            const r = await api.post("/admin/obblighi/resolve-single", body);
            const dest = r.data.dest_team_name || "—";
            const feeStr = r.data.fee > 0 ? ` per ${(r.data.fee / 1e6).toFixed(0)}mln` : " (gratis)";
            window.alert(`✓ ${r.data.player_name} risolto: ${r.data.kind === "loan_return" ? "Rientro prestito" : "Ceduto a " + dest + feeStr}`);
            onDone?.();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const dest = allTeams.find((t) => t.id === destTeamId);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="resolve-loan-modal">
            <div className="op-card max-w-lg w-full p-6 border-l-2 border-l-tff6b00">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="op-label text-tff6b00">// RISOLVI PRESTITO</div>
                        <h3 className="op-heading text-xl mt-1 text-tffffff">{target.name}</h3>
                        <div className="op-mono text-[11px] text-t8f95a3 mt-1">
                            Proprietario: <span className="text-tffffff">{ownerTeamName}</span>
                            {target.playing_team_name && <> · Attualmente a: <span className="text-tffffff">{target.playing_team_name}</span></>}
                        </div>
                    </div>
                    <button onClick={onClose} className="op-mono text-xs text-t8f95a3 hover:text-tffffff" data-testid="resolve-close">✕</button>
                </div>

                {/* Action toggle */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                        onClick={() => setAction("sell")}
                        className={`p-3 border text-left transition-colors ${action === "sell" ? "border-tff6b00 bg-tff6b00/10" : "border-t22252a hover:border-t8f95a3"}`}
                        data-testid="action-sell"
                    >
                        <div className={`op-label text-[10px] ${action === "sell" ? "text-tff6b00" : "text-t8f95a3"}`}>CEDUTO</div>
                        <div className="op-mono text-[11px] text-tffffff mt-1">Va in un&apos;altra squadra (gratis o con cifra)</div>
                    </button>
                    <button
                        onClick={() => setAction("return")}
                        className={`p-3 border text-left transition-colors ${action === "return" ? "border-t2bb6ff bg-t2bb6ff/10" : "border-t22252a hover:border-t8f95a3"}`}
                        data-testid="action-return"
                    >
                        <div className={`op-label text-[10px] ${action === "return" ? "text-t2bb6ff" : "text-t8f95a3"}`}>RIENTRA</div>
                        <div className="op-mono text-[11px] text-tffffff mt-1">Fine prestito, torna al proprietario</div>
                    </button>
                </div>

                {/* Sell fields */}
                {action === "sell" && (
                    <div className="space-y-3 mb-4">
                        <div>
                            <label className="op-label text-[10px] block mb-1">Squadra destinazione</label>
                            <select
                                value={destTeamId}
                                onChange={(e) => setDestTeamId(e.target.value)}
                                className="w-full bg-t0a0c0f border border-t22252a text-tffffff p-2 op-mono text-sm"
                                data-testid="resolve-destination"
                            >
                                <option value="">— Seleziona —</option>
                                {allTeams.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="op-label text-[10px] block mb-1">
                                Cifra (mln EUR) · 0 = ceduto gratis
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={feeMln}
                                onChange={(e) => setFeeMln(sanitizeDecimalInput(e.target.value))}
                                className="w-full bg-t0a0c0f border border-t22252a text-tffffff p-2 op-mono text-sm"
                                data-testid="resolve-fee"
                            />
                        </div>
                        {dest && feeMln > 0 && (
                            <div className="op-mono text-[11px] text-tc9ccd3 border border-tff6b00/40 bg-tff6b00/5 p-3">
                                <div className="text-tff6b00 uppercase text-[10px] mb-1.5">Anteprima movimento</div>
                                <div>{dest.name} pagherà <span className="text-tff3b30 font-bold">{(Number(feeMln) * 1_000_000).toLocaleString("it-IT")} €</span> ({Number(feeMln).toLocaleString("it-IT")} mln) a {ownerTeamName}</div>
                                <div className="text-t5c6370 mt-1">Il budget di {ownerTeamName} aumenterà di {Number(feeMln).toFixed(2)} mln.</div>
                            </div>
                        )}
                        {feeMln === 0 || feeMln === "0" || feeMln === "" ? (
                            <div className="op-mono text-[11px] text-t8f95a3 border border-t22252a p-2.5">
                                → Cessione gratuita. Nessun movimento di cassa.
                            </div>
                        ) : null}
                    </div>
                )}

                {action === "return" && (
                    <div className="op-mono text-[11px] text-tc9ccd3 border border-t22252a p-2.5 mb-4">
                        → Il giocatore tornerà sulla rosa principale di <span className="text-tffffff">{ownerTeamName}</span>. Nessun movimento di cassa.
                    </div>
                )}

                {err && (
                    <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-3" data-testid="resolve-error">
                        {err}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-t22252a">
                    <button onClick={onClose} className="op-btn px-3 py-1.5 text-xs" disabled={busy} data-testid="resolve-cancel">
                        Annulla
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy || (action === "sell" && !destTeamId)}
                        className="op-btn op-btn-primary px-4 py-1.5 text-xs disabled:opacity-50"
                        data-testid="resolve-confirm"
                    >
                        {busy ? "..." : "Conferma"}
                    </button>
                </div>
            </div>
        </div>
    );
}

